import fetch from 'isomorphic-fetch';
import { config } from '../config/config';
import store from '../redux/store';

/**
 * Calls loop-pro's `apps/backend` (`config.uri.lp_backend`) with the session's bearer token, and turns every failure
 * into an Error whose message a screen can show as is. The cb-client-admin-api response shapes are rebuilt in each
 * datasource, so the screens do not change (WI-2179).
 */

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  body?: any;
  form?: FormData;
  /** Another loop-pro service, such as public-api, that accepts the same token. Defaults to the backend. */
  base?: string;
}

export const NEEDS_ADMIN_MESSAGE =
  'You need admin access to make this change. Ask an administrator of your account to grant it.';

export class LpBackendError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'LpBackendError';
    this.status = status;
  }
}

const SESSION_KEY = 'cowabunga-client-admin';

/**
 * The store's token, or the saved session's: a page opened or reloaded directly fires its requests before
 * AccessComponent has copied the saved token into the store (child effects run before the parent's).
 */
const token = (): string => {
  const stored = store.getState().token.token;
  if (stored) return stored;
  try {
    return localStorage.getItem(SESSION_KEY) || '';
  } catch (err) {
    return '';
  }
};

const tokenPayload = (): any => {
  try {
    const part = (token() || '').split('.')[1] || '';
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch (err) {
    return {};
  }
};

/** The token's client id, for the `:client_id` segment the backend requires but ignores. */
export const clientId = (): string => {
  const payload = tokenPayload();
  return (
    payload.client?.id || payload.client_id || store.getState().me?.me?.organization?.id || 'me'
  );
};

/** The token's client-users id. */
export const userId = (): string => {
  const payload = tokenPayload();
  return payload.user?.id || payload.user_id || '';
};

/** `/client/:client_id` plus a path. */
export const clientPath = (path = ''): string => `/client/${clientId()}${path}`;

/**
 * A copy without `null` or `undefined` values: the backend answers 400 to a `null`. Booleans are converted where a
 * datasource knows the field is one (`toBoolean`), so string values such as tracking messages are sent as typed.
 */
export const clean = (value: any): any => {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined).map(clean);
  }
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== null && item !== undefined)
        .map(([key, item]) => [key, clean(item)])
    );
  }
  return value;
};

/** A real boolean from a form value. */
export const toBoolean = (value: any): boolean => value === true || value === 'true';

/** A number from a form value; a blank value stays out of the body. */
export const toNumber = (value: any): number | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  const number = Number(value);
  return Number.isNaN(number) ? undefined : number;
};

/** The id of a Firestore reference as the backend serialises it, or of a plain id or path string. */
export const refId = (ref: any): string | undefined => {
  if (!ref) return undefined;
  if (typeof ref === 'string') return ref.split('/').pop();
  if (typeof ref.id === 'string') return ref.id;
  const segments = ref._path?.segments || ref._key?.path?.segments;
  if (Array.isArray(segments) && segments.length) return segments[segments.length - 1];
  if (typeof ref.path === 'string') return ref.path.split('/').pop();
  return undefined;
};

const messageOf = (result: any): string => {
  const message = result?.message;
  // The exception filter appends the exception's own name ("Bad Request Exception") to validator messages.
  if (Array.isArray(message)) return message.filter((item) => !/Exception$/.test(item)).join('; ');
  if (typeof message === 'string') return message;
  if (typeof result?.error === 'string') return result.error;
  return '';
};

const errorMessage = (status: number, result: any): string => {
  const message = messageOf(result);
  if (status === 401) {
    // The guard answers 401 both to a missing scope ("You do not have permission ...") and to a bad or old token.
    if (/permission/i.test(message)) return NEEDS_ADMIN_MESSAGE;
    return 'Your session has expired. Please log in again.';
  }
  if (status === 403) {
    return message || 'You are not allowed to make this change.';
  }
  // The filter answers a crash on incomplete stored data (`TypeError`) with a 400 and a Firestore failure with a 409;
  // their messages are internals, so both read as server errors.
  if (status >= 500 || ['TYPE_ERROR', 'FIRESTORE_ERROR'].includes(result?.errorCode)) {
    return `Something went wrong on the server (${status}). Please try again.`;
  }
  return message || `The request failed (${status}).`;
};

export const lpRequest = async (
  method: Method,
  path: string,
  { body, form, base }: RequestOptions = {}
): Promise<any> => {
  const headers: Record<string, string> = { Authorization: `Bearer ${token()}` };
  if (!form) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${base || config.uri.lp_backend}${path}`, {
      method,
      headers: new Headers(headers),
      body: form || (body === undefined ? undefined : JSON.stringify(clean(body)))
    });
  } catch (err) {
    throw new LpBackendError('Could not reach the server. Check your connection and try again.', 0);
  }

  const text = await response.text();
  let result: any = {};
  try {
    result = text ? JSON.parse(text) : {};
  } catch (err) {
    result = { message: text };
  }

  if (!response.ok) {
    throw new LpBackendError(errorMessage(response.status, result), response.status);
  }

  return result;
};

/**
 * cb refused a duplicate name on hub rename, region create and rename, and branch create; those loop-pro routes do
 * not check, so the datasource checks the current list first (exact name, trimmed, as cb compared).
 */
export const assertUniqueName = (
  items: { id: string; name?: string }[],
  name: string,
  what: string,
  except?: string
): void => {
  const wanted = name.trim();
  if (items.some((item) => item.id !== except && (item.name ?? '').trim() === wanted)) {
    throw new LpBackendError(`A ${what} named "${name.trim()}" already exists.`, 409);
  }
};
